import React, { useState } from 'react';
import { Text } from '@teko/ecommerce/mobile';
import { styled, SvgIcon, ThemeProps } from '@teko/ui';
import {
  Flex,
  Quantity,
  ButtonIcon,
  Tooltip,
  LazyImage,
  Shape,
} from '@teko/ds-desktop';
import { ShoppingCartOutlined } from '@teko/ds-icon';

import { getTrackingData } from '@teko/tracking';
import { t } from 'i18n';
import { DiscoveryKeyword } from '@teko/discovery';

interface SearchListProps {
  suggestions: DiscoveryKeyword[];
  onSearch: (keywordObj: DiscoveryKeyword) => void;
  onHoverItem: (item: number) => void;
  selected: number | null;
  isSuggestProduct: boolean;
  onRedirectDetailProduct: (sku: string) => void;
  onAddToCart: (item: DiscoveryKeyword, quantity?: number) => void;
}

const StyledSearchItem = styled(Flex)(
  (props: { selected: boolean; isSuggestProduct: boolean }) => `
  display: flex;
  padding: 0.6rem;
  cursor: pointer;
  border-radius: 8px;
  align-items: center;
  justify-content: ${props.isSuggestProduct ? 'space-between' : 'none'} ;
  background: ${props.selected ? '#f5f5f5' : '#fff'};
  border-bottom: 1px solid #f4f4f4;
`,
);

const StyledCategoryName = styled.div(
  (props: ThemeProps) => `
  color: #82869E;
  font-size: 13px;
  .cat-name {
    color: ${props.theme.brand.primary};
  }
`,
);

const StyledIcon = styled(SvgIcon)`
  min-width: 20px;
`;

const StyledTextSuggestion = styled(Text)`
  text-overflow: ellipsis;
`;

export const SearchList: React.FC<SearchListProps> = ({
  suggestions,
  onSearch,
  onHoverItem,
  selected,
  onAddToCart,
  onRedirectDetailProduct,
  isSuggestProduct = false,
}: SearchListProps) => {
  return (
    <>
      {suggestions.map((s, index) => (
        <StyledSearchItem
          key={index}
          onClick={() =>
            !isSuggestProduct ? onSearch(s) : onRedirectDetailProduct(s?.sku)
          }
          selected={index === selected}
          onMouseEnter={() => onHoverItem(index)}
          isSuggestProduct={isSuggestProduct}
          {...getTrackingData({
            isTrackContent: true,
            regionName: 'searchSuggestion',
            contentName: s.query,
            target: 'searchProductResult',
            payload: JSON.stringify({
              screenName: 'searchProduct',
              index,
            }),
          })}
        >
          {isSuggestProduct && (
            <ProductSuggestItem product={s} onAddToCart={onAddToCart} />
          )}

          {!isSuggestProduct && (
            <>
              <StyledIcon name="search" color="#333" size={20} />
              <div style={{ margin: '0 0.6rem' }}>
                <Text maxLines={1}>{s?.query}</Text>
                {!!s?.categoryId && (
                  <StyledCategoryName>
                    Trong <span className="cat-name">{s.categoryName}</span>
                  </StyledCategoryName>
                )}
              </div>
            </>
          )}
        </StyledSearchItem>
      ))}
    </>
  );
};

const ProductInfoWrapper = styled(Flex)`
  flex: 1;
  overflow: hidden;
`;
const ProductImageWrapper = styled(Shape)`
  min-width: 50px;
  width: 50px;
  position: relative;
`;
/** trust parent is flex */
const ProductSuggestItem = ({
  product,
  onAddToCart,
}: {
  product: DiscoveryKeyword;
  onAddToCart: SearchListProps['onAddToCart'];
}) => {
  const { imageUrl, highlight } = product;
  const [quantity, setQuantity] = useState(1);
  return (
    <React.Fragment>
      <ProductInfoWrapper gapX="s16" align="center">
        <ProductImageWrapper>
          <LazyImage
            aspectRatio={1}
            contain
            src={imageUrl}
            alt={highlight}
            borderRadius={5}
          />
        </ProductImageWrapper>
        <Tooltip title={highlight}>
          <StyledTextSuggestion maxLines={1}>{highlight}</StyledTextSuggestion>
        </Tooltip>
      </ProductInfoWrapper>
      <Flex gap="s16" align="center" gapX="s36" justify="space-between">
        <Quantity
          quantity={quantity}
          setQuantity={setQuantity}
          onClick={(e) => {
            e.stopPropagation && e.stopPropagation();
          }}
        />
        <Tooltip title={t('Thêm vào giỏ hàng')} placement="bottom">
          <ButtonIcon
            size={30}
            color="primary"
            onClick={(e) => {
              onAddToCart(product, quantity);
              e.stopPropagation && e.stopPropagation();
            }}
          >
            <ShoppingCartOutlined />
          </ButtonIcon>
        </Tooltip>
      </Flex>
    </React.Fragment>
  );
};
